import { _keys, exists, ImplementationMissingException, tsValidate_OptionalArray, tsValidateArray, tsValidateBoolean, tsValidateNumber, tsValidateOptional, tsValidateOptionalId, tsValidateOptionalObject, tsValidateRange, tsValidateRegexp, tsValidateResult, tsValidateString, tsValidateTimeRange, tsValidateUniqueId, tsValidateValue } from '@nu-art/ts-common';
import { AllExpressionTypes, ExpressionOutput_Range, ExpressionType_Deduction, ExpressionType_NormalRange, ExpressionType_Score, ExpressionTypesWithVariable, } from './consts.js';
import { AllDeltaLogic, AllInOperators, allLogicTypes, Atom_LeftHand_EntityType_Atom, Atom_LeftHand_EntityTypes, EmptyRightHandOperators, Expression_OutputTypes, LogicType_Add, LogicType_And, LogicType_Complex, LogicType_Equals, LogicType_GreaterOrEqual, LogicType_InSet, LogicType_NotEquals, LogicType_NotInSet, LogicType_Or, LogicType_StructuredFreeText, NormalRangeVarLogic, RightHandValues_NumericOperators, SampleIndex_All, SelfOperators } from '../../_enum/index.js';
import { Validator_TagGroup } from '../macro-tag/index.js';
const Validator_AssociatedVariable = (instance, parentObject) => {
    if (!parentObject?.expressionType)
        return tsValidateResult(instance, tsValidateOptionalId);
    if (!ExpressionTypesWithVariable.includes(parentObject.expressionType))
        return tsValidateResult(instance, tsValidateOptionalId);
    return tsValidateResult(instance, tsValidateUniqueId);
};
const validateColorValue = tsValidateRegexp(/^#([0-9A-Fa-f]{3,4}){1,2}$/);
const Validator_SeverityDeclaration = (instance, parentObj) => {
    if (parentObj?.expressionType !== ExpressionType_Score.dbKey)
        return;
    if (!parentObj?.associatedVar)
        return;
    return tsValidateResult(instance, tsValidateArray({
        severityValue: tsValidateUniqueId,
        color: validateColorValue
    }));
};
export const getExpressionValidator = (isMandatory = false) => {
    return (instance) => {
        const expressionInstance = instance;
        // ############################# Validator Utils #############################
        const checkExpressionType = (value) => {
            if (value[0] === value[1])
                return false;
            return expressionInstance?.expressionType !== ExpressionType_NormalRange.dbKey && value[0] !== value[1];
        };
        const Validator_ValueAsRange = (instance) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!exists(instance))
                return 'missing instance for validation';
            if (!Array.isArray(instance))
                return 'instance provided is not from type array';
            if (!instance.length)
                return 'Empty value provided';
            if (instance.length > 2)
                return 'instance has too many values';
            if (checkExpressionType(instance))
                return 'can\'t use range in expression not from normal range';
            if (instance[0] !== instance[1] || instance.every(value => value === 0))
                return;
            return 'All cases should keep same kind of input, this atom expecting number but got range.';
        };
        const Validator_ValueAsNumber = (instance) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!exists(instance))
                return 'missing instance for validation';
            if (!Array.isArray(instance))
                return 'instance provided is not from type array';
            if (!instance.length)
                return 'Empty value provided';
            if (instance.length > 2)
                return 'instance has too many values';
            if (instance[0] === instance[1])
                return;
            return 'All cases should keep same kind of output, this atom expecting range but got number.';
        };
        const getValueValidator = (firstContainer, valueType) => {
            return (instance) => {
                if (!isMandatory && !exists(instance))
                    return;
                if (!instance)
                    return 'No instance provided for validation';
                const containerRange = firstContainer[valueType];
                if (!containerRange) {
                    if (checkExpressionType(instance))
                        return 'can\'t use range in expression not from normal range';
                    return tsValidateResult(instance, tsValidateRange());
                }
                if (exists(firstContainer[valueType]) && containerRange[0] === containerRange[1])
                    return tsValidateResult(instance, Validator_ValueAsNumber);
                return tsValidateResult(instance, Validator_ValueAsRange);
            };
        };
        const Validator_AtomsLogic = (instance, parentObject) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!instance)
                return 'No instance provided for validation';
            if (instance === LogicType_StructuredFreeText)
                return;
            const baseAllowedOperators = [LogicType_Complex];
            //Validate if all output types are boolean
            if (parentObject?.atoms.every(atom => (!exists(atom.cases[0].value) && !exists(atom.fallbackValue) && (atom.cases[0].valueType !== 'enum' && atom.cases[0].valueType !== 'numeric')) || atom.cases[0].valueType === 'boolean')) {
                const relevantOperators = [...baseAllowedOperators, LogicType_Or, LogicType_And, LogicType_GreaterOrEqual];
                return relevantOperators.includes(instance) ? undefined : `Atoms output type doesn't fit logic.\nAvailable logics: ${relevantOperators.join(' , ')}`;
            }
            //Validate if all output types are numeric
            if (parentObject?.atoms.every(atom => (exists(atom.cases[0].value) && exists(atom.fallbackValue)) || atom.cases[0].valueType === 'numeric')) {
                const relevantOperators = [...baseAllowedOperators, LogicType_Add, LogicType_GreaterOrEqual];
                return relevantOperators.includes(instance) ? undefined : `Atoms output type doesn't fit logic.\nAvailable logics: ${relevantOperators.join(' , ')}`;
            }
            return baseAllowedOperators.includes(instance)
                ? undefined
                : `Atoms output type doesn't fit logic.\nAvailable logics: ${baseAllowedOperators.join(' , ')}`;
        };
        // ############################# Validate Left Hand #############################
        const Validator_LeftHand = (instance) => {
            if (!instance)
                return 'no instance provided for validation';
            if (instance.type === Atom_LeftHand_EntityType_Atom)
                return tsValidateResult(instance, {
                    id: tsValidateString(1, isMandatory),
                    type: tsValidateValue([...Atom_LeftHand_EntityTypes]),
                });
            return tsValidateResult(instance, {
                id: isMandatory ? tsValidateUniqueId : tsValidateOptionalId,
                type: tsValidateValue([...Atom_LeftHand_EntityTypes]),
            });
        };
        // ############################# Validate Right Hand #############################
        const Validator_RightHandEmptyArray = tsValidateArray(tsValidateOptional, isMandatory, 0);
        const Validator_RightHandInSet = tsValidateArray(tsValidateUniqueId, isMandatory, 1);
        const Validator_RightHandNumericValues = (instance) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!instance)
                return 'Missing instance for validation';
            if (!Array.isArray(instance))
                return 'Instance provided is not from type array';
            if (instance.length > 2)
                return 'Instance provided includes to many values';
            if (instance[0] !== instance[1])
                return `Invalid value, equality operator can't accept range`;
            return tsValidateResult(instance, tsValidateRange(isMandatory));
        };
        const Validator_EqualityOperators = (instance) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!instance)
                return 'Missing instance for validation';
            if (!Array.isArray(instance))
                return 'Instance provided is not from type array';
            if (instance.length > 2)
                return 'Instance provided includes to many values';
            if (instance.length === 1)
                return tsValidateResult(instance, tsValidateArray(tsValidateUniqueId, isMandatory));
            if (instance[0] !== instance[1])
                return `Invalid value, equality operator can't accept range`;
            return tsValidateResult(instance, tsValidateRange(isMandatory));
        };
        const Validator_ValidateInRange = (instance) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!exists(instance))
                return 'missing instance for validation';
            if (!Array.isArray(instance))
                return 'instance provided is not from type array';
            if (instance.length > 2)
                return 'instance has too many values';
            if (instance.length === 1)
                return tsValidateResult(instance[0], tsValidateUniqueId);
            if (instance[0] === instance[1])
                return `In operator is looking for a range, can't check in range over a single number`;
            return tsValidateResult(instance, tsValidateRange(isMandatory));
        };
        const Validator_RightHand = (instance, parentObject) => {
            if (!instance)
                return 'no instance provided for validation';
            if (!isMandatory && !parentObject?.caseOperator)
                return '';
            if (!parentObject?.caseOperator)
                return 'can\'t validate right hand without an operator';
            if (parentObject.caseOperator === LogicType_Equals || parentObject.caseOperator === LogicType_NotEquals)
                return tsValidateResult(instance, Validator_EqualityOperators);
            if (EmptyRightHandOperators.includes(parentObject.caseOperator))
                return tsValidateResult(instance, Validator_RightHandEmptyArray);
            if (parentObject.caseOperator === LogicType_InSet || parentObject.caseOperator === LogicType_NotInSet)
                return tsValidateResult(instance, Validator_RightHandInSet);
            if (AllInOperators.includes(parentObject.caseOperator))
                return tsValidateResult(instance, Validator_ValidateInRange);
            if (RightHandValues_NumericOperators.includes(parentObject.caseOperator))
                return tsValidateResult(instance, Validator_RightHandNumericValues);
            throw new ImplementationMissingException(`Missing validator for right hand value ${instance}`);
        };
        // ############################# Validate Cases #############################
        const Validator_GeneralCase = {
            rightHand: Validator_RightHand,
            caseOperator: tsValidateValue(allLogicTypes, isMandatory),
            value: tsValidateRange(isMandatory),
            valueType: tsValidateValue(['boolean', 'numeric', 'enum'], false),
            factor: tsValidateOptionalObject({
                upper: tsValidateNumber(isMandatory),
                lower: tsValidateNumber(isMandatory)
            })
        };
        const Validator_Cases = (instance) => {
            if (!instance)
                return 'no instance provided for validation';
            if (instance.length > 1) {
                return tsValidateResult(instance, tsValidateArray({
                    ...Validator_GeneralCase,
                    caseOperator: (instance) => {
                        if (!instance && !isMandatory)
                            return;
                        if (!instance)
                            return 'No instance provided to validation';
                        if (!allLogicTypes.filter(op => !SelfOperators.includes(op)).includes(instance))
                            return 'Operator is not allowed in this context';
                    },
                    value: getValueValidator(instance[0], 'value'),
                    factor: tsValidateOptionalObject({
                        upper: tsValidateNumber(isMandatory),
                        lower: tsValidateNumber(isMandatory)
                    })
                }));
            }
            if (instance.length === 1 && exists(instance[0].value) && NormalRangeVarLogic.includes(instance[0].caseOperator)) {
                return tsValidateResult(instance, tsValidateArray({
                    ...Validator_GeneralCase,
                    value: getValueValidator(instance[0], 'value'),
                    factor: tsValidateOptionalObject({
                        upper: tsValidateNumber(isMandatory),
                        lower: tsValidateNumber(isMandatory)
                    })
                }));
            }
            return tsValidateResult(instance, tsValidateArray({
                ...Validator_GeneralCase,
                value: getValueValidator(instance[0], 'value')
            }));
        };
        // ############################# Validate Term And Atoms #############################
        const term_isFlatTerm = (_term) => {
            return _term.atomsLogic !== LogicType_Complex;
        };
        const term_isWithSubTerms = (_term) => {
            return _term.atomsLogic === LogicType_Complex;
        };
        const Validator_Atom = (instance) => {
            return tsValidateResult(instance, {
                fallbackValue: getValueValidator(instance?.cases[0], 'value'),
                cases: Validator_Cases,
                leftHand: Validator_LeftHand,
                deltaTimeDiff: (deltaTimeDiff) => {
                    if (!deltaTimeDiff && !isMandatory)
                        return;
                    if (!deltaTimeDiff)
                        return 'No instance provided for validation';
                    if (!AllDeltaLogic.includes(instance?.cases[0].caseOperator))
                        return 'Cannot apply delta time difference to a non delta atom';
                    return tsValidateResult(deltaTimeDiff, tsValidateTimeRange(false));
                },
                sampleInfo: tsValidateOptionalObject({
                    sampleIndex: tsValidateValue(SampleIndex_All),
                    timeConstraint: (instance) => {
                        if (!instance && !isMandatory)
                            return;
                        if (!instance)
                            return 'No instance provided for validation';
                        if (!instance.length)
                            return;
                        return tsValidateResult(instance, tsValidateTimeRange(false));
                    },
                    expiry: tsValidateTimeRange(false),
                }),
                timestamp: tsValidateOptional,
                comment: tsValidateString(undefined, isMandatory)
            });
        };
        const Validator_BaseTerm = {
            expression: tsValidateString(undefined, isMandatory),
            outputType: tsValidateValue(Expression_OutputTypes, false),
            structuredFreeText: tsValidateOptionalObject({
                if: tsValidateString(),
                then: tsValidateString(),
                else: tsValidateString()
            }),
            comment: tsValidateString(undefined, false),
            atoms: tsValidateArray(Validator_Atom)
        };
        const Validator_SubTerms = (instance, parentObject) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!instance)
                return 'No instance provided for validation';
            const subTerms = parentObject?.subTerms;
            if (!exists(subTerms) || !subTerms.length)
                return;
            return tsValidateResult(instance, tsValidateArray({
                ...Validator_BaseTerm,
                atomsLogic: tsValidateValue([LogicType_And, LogicType_Or], isMandatory),
                fallbackValue: getValueValidator(subTerms[0], 'fallbackValue'),
                value: getValueValidator(subTerms[0], 'value')
            }));
        };
        const Validator_Term = (instance) => {
            if (!isMandatory && !exists(instance))
                return;
            if (!instance)
                return 'No instance provided for validation';
            if (term_isWithSubTerms(instance)) {
                return tsValidateResult(instance, {
                    ...Validator_BaseTerm,
                    subTerms: Validator_SubTerms,
                    fallbackValue: getValueValidator(instance.subTerms?.[0], 'value'),
                    value: getValueValidator(instance.subTerms?.[0], 'value'),
                    atomsLogic: Validator_AtomsLogic
                });
            }
            if (term_isFlatTerm(instance)) {
                const firstContainer = exists(instance.atoms[0].cases[0].value) ? instance.atoms[0].cases[0] : instance;
                return tsValidateResult(instance, {
                    ...Validator_BaseTerm,
                    atomsLogic: Validator_AtomsLogic,
                    fallbackValue: (currentValue) => {
                        if ((exists(instance.atoms[0].cases[0].value) && instance.atoms.length === 1) && exists(currentValue))
                            return 'Can\'t have term else value in a single atom term with value, it\'s redundant';
                        return (getValueValidator(firstContainer, 'value'))(currentValue);
                    },
                    value: (currentValue) => {
                        if (instance.atomsLogic === LogicType_GreaterOrEqual && !currentValue)
                            return 'At Least requires term value';
                        if ((exists(instance.atoms[0].cases[0].value) && instance.atoms.length === 1) && exists(currentValue))
                            return 'Can\'t have term value in a single atom term with value, it\'s redundant';
                        return (getValueValidator(instance.atoms?.[0]?.cases?.[0], 'value'))(currentValue);
                    },
                    outputType: (instance, parentInstance) => {
                        if (!instance && parentInstance?.atomsLogic === LogicType_StructuredFreeText)
                            return 'When using the free text logic output type must be provided';
                        if (!isMandatory && !exists(instance))
                            return;
                        if (!instance)
                            return 'No instance provided for validation';
                        if (instance === ExpressionOutput_Range && expressionInstance?.expressionType !== ExpressionType_NormalRange.dbKey) {
                            return 'Cannot return range as output type if expression is not normal range';
                        }
                        return Expression_OutputTypes.includes(instance) ? undefined : `output type ${instance} not in ${Expression_OutputTypes.join(',')}`;
                    }
                });
            }
            throw new ImplementationMissingException(`Missing validator for term  ${instance}`);
        };
        // ############################# Validate Expression Terms #############################
        const TermsOutput_IsNumeric = (term) => {
            if (term.atomsLogic === LogicType_Complex)
                return true;
            if (term.atomsLogic === '>=')
                return false;
            if (exists(term.value))
                return true;
            if (term.outputType === 'numeric')
                return true;
            return term.atoms.every(atom => {
                if (exists(atom.cases[0].value))
                    return true;
                return atom.cases[0].valueType === 'numeric';
            });
        };
        const TermOutput_IsBoolean = (term) => {
            if (term.atomsLogic === LogicType_Complex)
                return false;
            if (term.atomsLogic === '>=')
                return true;
            if (exists(term.value))
                return false;
            if (term.outputType === 'boolean')
                return true;
            return !term.atoms.every(atom => exists(atom.cases[0].value)
                || atom.cases[0].valueType === 'numeric' || atom.cases[0].valueType === 'enum');
        };
        const Validator_Terms = (terms) => {
            const innerTermsValidator = tsValidateResult(terms, tsValidateArray(Validator_Term));
            if (terms?.every(term => TermsOutput_IsNumeric(term))) {
                return innerTermsValidator;
            }
            if (terms?.every(term => TermOutput_IsBoolean(term))) {
                return innerTermsValidator;
            }
            return 'All Terms should be with the same output type, either numeric boolean or range';
        };
        // ############################# Validate value mapping #############################
        const Validator_ValueMapping = (valueMapping, parentObj) => {
            const isDeductionExpression = parentObj?.expressionType === ExpressionType_Deduction.dbKey;
            // shouldn't exists pass
            if (!valueMapping && !isDeductionExpression)
                return;
            // exists but shouldn't
            if (valueMapping && !isDeductionExpression)
                return `Non deduction expression should not have value mapping. expression type: ${parentObj?.expressionType}`;
            // validate mapping content
            const _valueMapping = valueMapping;
            _keys(_valueMapping).forEach(key => {
                if (tsValidateResult(key, tsValidateUniqueId))
                    return `Invalid key: ${key}`;
                return tsValidateResult(_valueMapping[key], tsValidateArray(tsValidateUniqueId));
            });
        };
        // ############################# Validate Expression #############################
        return tsValidateResult(instance, {
            label: tsValidateString(),
            expressionType: tsValidateValue([...AllExpressionTypes, ExpressionType_NormalRange].map(expressionType => expressionType.dbKey)),
            associatedVar: Validator_AssociatedVariable,
            severityDeclarations: Validator_SeverityDeclaration,
            outputType: tsValidateValue(Expression_OutputTypes, isMandatory),
            advisorFormKey: tsValidateString(undefined, isMandatory),
            expression: tsValidateString(undefined, isMandatory),
            isEvidenceBased: tsValidateBoolean(isMandatory),
            description: tsValidateString(undefined, isMandatory),
            references: tsValidate_OptionalArray(tsValidateUniqueId),
            terms: Validator_Terms,
            valueMapping: Validator_ValueMapping,
            tagGroups: tsValidateArray(Validator_TagGroup),
        });
    };
};
export const Validator_ExpressionMinimalAssertion = {
    label: tsValidateString(),
    expressionType: tsValidateValue(AllExpressionTypes.map(expressionType => expressionType.dbKey)),
    associatedVar: Validator_AssociatedVariable,
    severityDeclarations: tsValidateOptional,
    outputType: tsValidateOptional,
    advisorFormKey: tsValidateOptional,
    expression: tsValidateOptional,
    isEvidenceBased: tsValidateOptional,
    description: tsValidateOptional,
    references: tsValidateOptional,
    terms: tsValidateOptional,
    valueMapping: tsValidateOptional,
    tagGroups: tsValidateArray(Validator_TagGroup),
};
//# sourceMappingURL=validator.js.map